import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Plus, Image as ImageIcon, Send, CheckCircle2, Clock, X, Loader2, History } from "lucide-react";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const TYPE_ICON = { competition: "🥇", seminaire: "🎓", demonstration: "🥋", jeux: "🏆", social: "🎉", evenement: "📅" };

export default function CalendrierAnnuelModule() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async (y) => {
    setLoading(true);
    try { const { data } = await api.get(`/accueil-pilotage/calendrier?year=${y}`); setData(data); }
    catch { toast.error("Chargement du calendrier échoué."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(year); }, [load, year]);

  const publish = async (e) => {
    if (e.source !== "dojo_event") { toast.info("Cet événement est déjà publié."); return; }
    try { await api.put(`/accueil-pilotage/calendrier/event/${e.id}`, { status: e.status === "published" ? "brouillon" : "published" }); load(year); }
    catch { toast.error("Erreur"); }
  };

  if (loading || !data) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" /> Chargement…</div>;

  const visible = data.events.filter((e) => showPast || !e.is_past);
  const byMonth = {};
  visible.forEach((e) => { (byMonth[e.month] = byMonth[e.month] || []).push(e); });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="calendrier-annuel-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[#C9A961] text-xs uppercase tracking-widest">Accueil & Pilotage</div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2"><CalendarDays className="w-7 h-7 text-[#C9A961]" /> Calendrier annuel</h1>
          <p className="text-zinc-400 text-sm mt-1">{data.count} événement(s) · {data.published} publié(s){data.sans_affiche > 0 && ` · ${data.sans_affiche} sans affiche`}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="cal-year"
            className="bg-zinc-900 border border-white/10 rounded-xl px-3 py-2 text-sm text-white">
            {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setShowPast((v) => !v)} data-testid="cal-toggle-past"
            className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1 ${showPast ? "bg-white/15 text-white" : "bg-white/5 text-zinc-400"}`}>
            <History className="w-4 h-4" /> Historique
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="cal-add-btn"
            className="px-4 py-2 rounded-xl bg-[#C9A961] hover:bg-white text-black font-semibold text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Ajouter</button>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-zinc-500 text-sm py-10 text-center">Aucun événement pour {year}. Ajoute-en un ci-dessus.</p>
      ) : (
        <div className="space-y-5" data-testid="cal-months">
          {MONTHS.map((mName, idx) => {
            const evs = byMonth[idx + 1];
            if (!evs || evs.length === 0) return null;
            return (
              <div key={idx} data-testid={`cal-month-${idx + 1}`}>
                <div className="text-[#C9A961] font-semibold text-sm uppercase tracking-wide mb-2">{mName} {year}</div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {evs.map((e) => (
                    <div key={e.id} className={`rounded-2xl border p-4 ${e.is_past ? "border-white/5 bg-white/[0.01] opacity-70" : "border-white/10 bg-white/[0.03]"}`} data-testid={`cal-event-${e.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white font-semibold text-sm">{TYPE_ICON[e.type] || "📅"} {e.title}</div>
                          <div className="text-[11px] text-zinc-500">{e.start_date}{e.end_date && e.end_date !== e.start_date ? ` → ${e.end_date}` : ""}{e.location && ` · ${e.location}`}</div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${e.status === "published" ? "bg-emerald-600/30 text-emerald-200" : "bg-amber-600/30 text-amber-200"}`}>{e.status === "published" ? "Publié" : "Brouillon"}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {!e.has_poster && <button onClick={() => nav("/app/generateur-affiches")} data-testid={`cal-poster-${e.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-pink-600/30 text-pink-200 hover:bg-pink-600/50 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Créer affiche</button>}
                        <button onClick={() => nav("/app/centre-suivi")} data-testid={`cal-comm-${e.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-sky-600/30 text-sky-200 hover:bg-sky-600/50 flex items-center gap-1"><Send className="w-3 h-3" /> Communication</button>
                        {e.source === "dojo_event" && (
                          <button onClick={() => publish(e)} data-testid={`cal-publish-${e.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 flex items-center gap-1">
                            {e.status === "published" ? <><Clock className="w-3 h-3" /> Dépublier</> : <><CheckCircle2 className="w-3 h-3" /> Publier</>}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddEventModal onClose={() => setShowAdd(false)} reload={() => load(year)} />}
    </div>
  );
}

function AddEventModal({ onClose, reload }) {
  const [f, setF] = useState({ title: "", start_date: "", end_date: "", event_type: "evenement", location: "", status: "published", short_description: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim() || !f.start_date) { toast.error("Titre et date requis."); return; }
    setBusy(true);
    try { await api.post("/accueil-pilotage/calendrier/event", f); toast.success("Événement ajouté au calendrier."); reload(); onClose(); }
    catch { toast.error("Erreur"); } setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="cal-add-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900 p-6 space-y-3">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-white">Ajouter un événement</h3><button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button></div>
        <input autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titre de l'événement" data-testid="cal-title" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[11px] text-zinc-500">Début</label><input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="cal-start" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200" /></div>
          <div><label className="text-[11px] text-zinc-500">Fin (opt.)</label><input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="cal-end" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })} data-testid="cal-type" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200">
            <option value="evenement">Événement</option><option value="competition">Compétition</option><option value="seminaire">Séminaire</option><option value="demonstration">Démonstration</option><option value="jeux">Jeux Martiaux</option><option value="social">Social</option>
          </select>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="cal-status" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200">
            <option value="published">Publié</option><option value="brouillon">Brouillon</option>
          </select>
        </div>
        <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Lieu (optionnel)" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <button onClick={save} disabled={busy} data-testid="cal-save" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2 rounded-lg text-sm disabled:opacity-50">{busy ? "…" : "Ajouter au calendrier"}</button>
      </div>
    </div>
  );
}
